/* The v13 card (locked 2026-08-31) — spec of record:
 * mocks-2026-08-31/mock-card-logic.html (cases A–K).
 *
 * One card = one instrument's chain segment. Title = stage chip ONLY;
 * body = CLAIMS (one plain sentence per extracted object, own doc
 * chips, mono state badge); booking cards carry the ownership CHART
 * (holders → instrument → issuer, quantities written on the links).
 * Charts are LIVE: claim↔chart hover linkage, EDIT changes flow into
 * the chart, ticking a register holder draws its arrow and drops its
 * claims in. SPA rule: a stranger counterparty renders one greyed
 * NOT-BOOKED line and never books. */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '../../lib/api'
import { approveDocs, keepSeparate, markFundVehicle, mergeObjects, snoozeTask } from './api'
import type {
  ApproveDocsRow,
  Claim,
  ClaimChip,
  ClaimPart,
  DeckCard,
  DeckResponse,
  SegmentChart,
} from './types'
import type { OpenDocPayload } from './RkCard'
import RkCandidates from './RkCandidates'
import { decideSides } from './decideSides'
import { HelpDot } from '../guidance/Guidance'
import Sentence from '../Sentence'
import '../canon.css'

interface Props {
  card: DeckCard
  deck: DeckResponse
  onDone: (slimText: string) => void
  onOpenDoc: (payload: OpenDocPayload) => void
  onRefresh?: () => void
  onPendingChange?: (pending: boolean) => void
}

function errDetail(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: unknown } | undefined
    if (body && typeof body.detail === 'string') return body.detail
    return `${err.status} ${err.statusText}`
  }
  return err instanceof Error ? err.message : 'request failed'
}

const DOT_CLASS: Record<string, string> = {
  entity: 'c-ent',
  instrument: 'c-ins',
  position: 'c-pos',
}

const BADGE: Record<string, { cls: string; label: string }> = {
  new: { cls: 'st-new', label: 'NEW' },
  on_record: { cls: 'st-rec', label: 'ON RECORD' },
  update: { cls: 'st-upd', label: 'UPDATE' },
}

/** The type symbol leading a claim line — dot for objects, arrow for
 * trades (the arrow takes the color of what the trade moves). */
function ClaimSymbol({ claim }: { claim: Claim }) {
  if (claim.object_type === 'trade') {
    return <span className={`cn-tarr ${claim.moves === 'instrument' ? 'ti' : 'tp'}`}>➜</span>
  }
  return <span className={`cn-tdot ${DOT_CLASS[claim.object_type]}`} />
}

export default function RkClaimsCard({
  card,
  deck,
  onDone,
  onOpenDoc,
  onRefresh,
  onPendingChange,
}: Props) {
  const claims = useMemo(() => card.claims ?? [], [card.claims])
  const chart = card.chart ?? null

  /* Register tick state — anchored holders start ticked and stay so. */
  const [ticks, setTicks] = useState<Record<string, boolean>>(() => {
    const t: Record<string, boolean> = {}
    for (const h of chart?.holders ?? []) t[h.key] = h.ticked ?? true
    return t
  })
  const [editing, setEditing] = useState(false)
  const [edits, setEdits] = useState<Record<string, Record<string, string>>>({})
  const [hover, setHover] = useState<string | null>(null)
  const [targetIdx, setTargetIdx] = useState(0)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [discardReason, setDiscardReason] = useState('')
  const [twinBlockedIds, setTwinBlockedIds] = useState<string[]>([])
  /* Snapshot-isolation refusal (Ale 2026-09-02): 409 deck_changed when a
   * document processed while the officer was on this card — nothing booked.
   * Arms a "Reload card" affordance so he SEES the change before re-deciding. */
  const [deckChanged, setDeckChanged] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const pendingCallback = useRef(onPendingChange)
  pendingCallback.current = onPendingChange
  useEffect(() => {
    pendingCallback.current?.(busy)
  }, [busy])
  useEffect(() => () => pendingCallback.current?.(false), [])
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const holderTicked = (key: string | null | undefined) => !key || (ticks[key] ?? true)
  const visibleClaims = claims.filter((c) => holderTicked(c.holder_key))
  const bookClaims = visibleClaims.filter(
    (c) => c.proposal_id && c.bookable !== false && !c.not_booked
  )
  const tickableHolders = (chart?.holders ?? []).filter((h) => h.tickable)
  const tickedCount = tickableHolders.filter((h) => ticks[h.key]).length
  const pickRequired = !!chart?.pick_required && tickedCount === 0
  const isMergeQuestion =
    card.stage === 'decide' && !!card.question && (card.actions ?? []).includes('merge')

  const chips = useMemo(() => {
    const seen = new Map<string, ClaimChip>()
    for (const c of visibleClaims) for (const ch of c.chips ?? []) seen.set(ch.label, ch)
    return [...seen.values()]
  }, [visibleClaims])

  /* EDIT flows into the chart live: the edited figure overrides the
   * matching link label. */
  const chartOverride = (key: string, kind: 'pos' | 'trd'): string | null => {
    for (const c of claims) {
      if (c.chart_ref !== `${kind}:${key}`) continue
      const e = edits[c.claim_id]
      if (!e) continue
      if (kind === 'pos' && e.units_held) return e.units_held
      if (kind === 'trd' && (e.units_delta || e.effective_date)) {
        const h = (chart?.holders ?? []).find((x) => x.key === key)
        const base = h?.trade_label ?? ''
        const [qty, date] = base.split(' · ')
        const sign = qty?.startsWith('−') ? '−' : '+'
        return `${e.units_delta ? sign + e.units_delta.replace(/^[+−-]/, '') : qty} · ${
          e.effective_date ?? date ?? ''
        }`.replace(/ · $/, '')
      }
    }
    return null
  }

  const openChip = (chip: ClaimChip, highlight?: string) => {
    onOpenDoc({
      title: chip.label,
      documentId: chip.document_id ?? deck.document_id,
      page: chip.page,
      snippet: chip.snippet,
      highlight,
      language: card.source_language,
    })
  }

  const openSources = () => {
    if (chips.length) openChip(chips[0])
    else if (deck.document_id) onOpenDoc({ title: 'Source document', documentId: deck.document_id })
  }

  const settle = (slim: string) => onDone(slim)

  const submitApprove = async (mode: 'approve' | 'continue') => {
    if (!deck.group_id) return
    setBusy(true)
    setError(null)
    try {
      /* One PROPOSAL can carry several claims (an update deck renders one
       * claim per changed FIELD) — the decisions payload must carry each
       * proposal ONCE or the API refuses ("Duplicate proposal_id in
       * decisions"; 2026-08-31 walk finding: every multi-field update
       * deck was un-approvable and blocked its queue). Dedupe by id:
       * booking beats leave_pending, edited fields union. */
      const byId = new Map<string, ApproveDocsRow>()
      const push = (row: ApproveDocsRow) => {
        const prev = byId.get(row.proposal_id)
        if (!prev) {
          byId.set(row.proposal_id, { ...row })
          return
        }
        if (prev.action === 'leave_pending' && row.action !== 'leave_pending')
          prev.action = row.action
        if (row.edited_fields)
          prev.edited_fields = { ...(prev.edited_fields ?? {}), ...row.edited_fields }
      }
      for (const c of claims) {
        if (!c.proposal_id) continue
        const booking =
          mode === 'approve' && holderTicked(c.holder_key) && c.bookable !== false && !c.not_booked
        const action = booking
          ? twinBlockedIds.includes(c.proposal_id)
            ? 'resolve'
            : 'approve'
          : 'leave_pending'
        const edited = booking && edits[c.claim_id] ? edits[c.claim_id] : undefined
        push({
          proposal_id: c.proposal_id,
          action,
          ...(edited ? { edited_fields: edited } : {}),
        })
        for (const extra of c.also_books ?? [])
          push({ proposal_id: extra, action: booking ? 'approve' : 'leave_pending' })
      }
      const rows: ApproveDocsRow[] = [...byId.values()]
      if (!rows.length) {
        settle('✓ nothing to book on this card')
        return
      }
      const res = await approveDocs(deck.group_id, rows, deck.deck_version)
      const booked = res.outcomes.filter((o) => o.status === 'confirmed').length
      setTwinBlockedIds([])
      setDeckChanged(false)
      settle(
        mode === 'continue'
          ? '→ continued — the register’s other holders wait'
          : `✓ approved ${booked || bookClaims.length} record${(booked || bookClaims.length) === 1 ? '' : 's'}`
      )
    } catch (err) {
      const detail = errDetail(err)
      if (err instanceof ApiError && err.status === 409) {
        // Snapshot-isolation refusal is a 409 too — disambiguate on the code
        // before the twin-guard id parse (a deck_changed detail has no ids).
        if (detail.includes('deck_changed')) {
          setDeckChanged(true)
          setError(detail.replace(/^deck_changed:\s*/, ''))
          return
        }
        const ids = detail.match(/[0-9a-f]{8}-[0-9a-f-]{27}/g) ?? []
        if (ids.length) {
          setTwinBlockedIds(ids)
          setError(`${detail} — Approve again to book the blocked rows anyway, as distinct events.`)
          return
        }
      }
      setError(detail)
    } finally {
      setBusy(false)
    }
  }

  const submitDiscard = async () => {
    if (!deck.group_id || !discardReason.trim()) return
    setBusy(true)
    setError(null)
    try {
      /* ONE decision per proposal — the server 400s on duplicates, and a
       * multi-field update deck renders several claims off one proposal
       * (same class as the approve fix above; review catch 2026-08-31:
       * Discard on such a deck died on the duplicate check). */
      const byId = new Map<string, ApproveDocsRow>()
      for (const c of visibleClaims) {
        if (!c.proposal_id || c.not_booked) continue
        byId.set(c.proposal_id, {
          proposal_id: c.proposal_id,
          action: 'reject',
          rejection_justification: discardReason.trim(),
        })
        for (const extra of c.also_books ?? [])
          byId.set(extra, {
            proposal_id: extra,
            action: 'reject',
            rejection_justification: discardReason.trim(),
          })
      }
      const rows = [...byId.values()]
      if (rows.length) await approveDocs(deck.group_id, rows, deck.deck_version)
      settle('✗ discarded — reason recorded under your name')
    } catch (err) {
      const detail = errDetail(err)
      if (detail.includes('deck_changed')) {
        setDeckChanged(true)
        setError(detail.replace(/^deck_changed:\s*/, ''))
      } else {
        setError(detail)
      }
    } finally {
      setBusy(false)
    }
  }

  const submitMerge = async () => {
    const q = card.question
    if (!q?.proposal_id) return
    const target = (q.candidates ?? [])[targetIdx] as
      { entity_id?: string; instrument_id?: string } | undefined
    setBusy(true)
    setError(null)
    try {
      await mergeObjects({
        source_proposal_ids: [q.proposal_id],
        ...(target?.instrument_id ? { target_instrument_id: target.instrument_id } : {}),
        ...(target?.entity_id ? { target_entity_id: target.entity_id } : {}),
      })
      settle('✓ merged — the wording is remembered as an alias')
    } catch (err) {
      setError(errDetail(err))
    } finally {
      setBusy(false)
    }
  }

  const submitKeepSeparate = async () => {
    const q = card.question
    if (!q?.proposal_id) return
    setBusy(true)
    setError(null)
    try {
      await keepSeparate([q.proposal_id])
      settle('✓ kept separate — both records stand')
    } catch (err) {
      setError(errDetail(err))
    } finally {
      setBusy(false)
    }
  }

  const submitSnooze = async () => {
    if (!card.task_id) return
    setBusy(true)
    try {
      await snoozeTask(card.task_id, 'next_document')
      settle('zz snoozed until the next document')
    } catch (err) {
      setError(errDetail(err))
    } finally {
      setBusy(false)
    }
  }

  const markFund = async (c: Claim) => {
    if (!deck.group_id || !c.proposal_id) return
    setBusy(true)
    setError(null)
    try {
      const r = await markFundVehicle(deck.group_id, c.proposal_id)
      setNotice(
        r.created
          ? `${r.fund_name} materialized as a fund — it now heads its chain. Now approve the claims.`
          : `${r.fund_name} already exists as a fund — nothing new created.`
      )
      onRefresh?.()
    } catch (err) {
      setError(errDetail(err))
    } finally {
      setBusy(false)
    }
  }

  const stage = card.stage === 'decide' ? 'Decide' : 'Book'
  const kCls = card.stage === 'decide' ? 'k q' : 'k'

  /* ── the register questions ride the card (mock B / B2), as SENTENCES:
   * the holder count is a datum, the instrument a datum. ──────────────── */
  const registerQuestion: { meaning: string; count: number } | null = chart?.pick_required
    ? { meaning: 'card.register_pick', count: tickableHolders.length }
    : tickableHolders.length
      ? { meaning: 'card.register_belong', count: tickableHolders.length }
      : null

  const mergeCandidates = (card.question?.candidates ?? []) as {
    name?: string
    entity_id?: string
    instrument_id?: string
  }[]

  /* The claim lines the card prints under its question (see the block
   * below): everything, except the pair the candidate comparison
   * already carries verbatim. */
  const claimLines = isMergeQuestion && mergeCandidates.length <= 1 ? [] : visibleClaims

  const primaryLabel = pickRequired
    ? 'Pick your chain first'
    : bookClaims.length === 0 && tickableHolders.length
      ? 'Continue — my fund only'
      : bookClaims.length > 1
        ? `Approve ${bookClaims.length} records`
        : 'Approve'

  return (
    <div
      className="cnv"
      data-testid="claims-card"
      data-card-id={card.card_id}
      data-stage={card.stage}
    >
      <div className="cn-back" />
      <div className={`cn-card${editing ? ' editing' : ''}`}>
        <div className="cn-ctit">
          <span className={kCls}>{stage}</span>
          <button type="button" className="cn-opensrc" onClick={openSources}>
            Open sources ({Math.max(chips.length, 1)}) 📄
          </button>
        </div>

        {chart ? (
          <ChartView
            chart={chart}
            ticks={ticks}
            onTick={(key) => setTicks((t) => ({ ...t, [key]: !t[key] }))}
            hover={hover}
            setHover={setHover}
            override={chartOverride}
          />
        ) : null}

        {isMergeQuestion ? (
          <div className="cn-qblock" data-testid="claims-question">
            <div className="qq">
              {mergeCandidates.length
                ? `Is "${card.question?.label}" the same ${
                    mergeCandidates[0]?.instrument_id ? 'instrument' : 'entity'
                  } as "${mergeCandidates[targetIdx]?.name ?? mergeCandidates[0]?.name}"?`
                : `Is "${card.question?.label}" already on your record?`}
            </div>
            <div className="qw">
              Merging folds them into one record and remembers the wording as an alias; keeping
              separate records both.
            </div>
          </div>
        ) : registerQuestion ? (
          <div className="cn-qblock" data-testid="register-question">
            <Sentence
              as="div"
              className="qq sn-lead"
              meaning={registerQuestion.meaning}
              values={{
                count: registerQuestion.count,
                instrument: chart?.instrument_name ?? null,
              }}
            />
          </div>
        ) : null}

        {/* D1: the two candidates side by side — the officer was being
         * asked "same or not?" with two names and nothing else. When the
         * record offers ONE candidate the two blocks below say exactly
         * what the claim lines said, with the wording and the figures
         * added, so the lines would be the same sentence twice; with
         * several candidates the lines stay, because the retarget radio
         * rides them. */}
        {isMergeQuestion ? (
          <RkCandidates
            sides={decideSides(card, targetIdx)}
            onOpenChip={(chip, side) => openChip(chip, side.name)}
          />
        ) : null}

        {claimLines.map((c) => (
          <div
            key={c.claim_id}
            className={`cn-cl${c.not_booked ? ' notbooked' : ''}${
              c.chart_ref && hover === c.chart_ref ? ' hlr' : ''
            }`}
            data-testid="claim-line"
            onMouseEnter={() => c.chart_ref && setHover(c.chart_ref)}
            onMouseLeave={() => setHover(null)}
          >
            <ClaimSymbol claim={c} />
            <span className="txt">
              {c.parts.map((p, i) => (
                <ClaimPartView
                  key={i}
                  part={p}
                  editing={editing && !!p.editable_field && c.bookable !== false}
                  value={
                    edits[c.claim_id]?.[p.editable_field ?? ''] ??
                    (editing ? (p.edit_value ?? p.text) : p.text)
                  }
                  onEdit={(v) =>
                    setEdits((e) => ({
                      ...e,
                      [c.claim_id]: { ...e[c.claim_id], [p.editable_field ?? '']: v },
                    }))
                  }
                />
              ))}
              {isMergeQuestion && c.state === 'on_record' && mergeCandidates.length > 1 ? (
                <label style={{ marginLeft: 8, fontSize: 'var(--text-12)' }}>
                  <input
                    type="radio"
                    name="merge-target"
                    checked={mergeCandidates[targetIdx]?.name === c.parts.find((p) => p.bold)?.text}
                    onChange={() =>
                      setTargetIdx(
                        mergeCandidates.findIndex(
                          (m) => m.name === c.parts.find((p) => p.bold)?.text
                        )
                      )
                    }
                  />{' '}
                  merge into this
                </label>
              ) : null}
              {c.fund_vehicle_candidate ? (
                <button
                  type="button"
                  className="cn-btn"
                  style={{ marginLeft: 8, padding: '1px 10px', fontSize: 'var(--text-12)' }}
                  disabled={busy}
                  onClick={() => markFund(c)}
                  data-testid="mark-fund"
                >
                  Mark as fund
                </button>
              ) : null}
              {c.register_missing ? (
                <small style={{ marginLeft: 8 }}>
                  — no agreement on the register for this fund — update the register
                </small>
              ) : null}
            </span>
            <span className="right">
              {(c.chips ?? []).map((ch) => (
                <button
                  key={ch.label}
                  type="button"
                  className="cn-docchip"
                  onClick={() =>
                    openChip(
                      ch,
                      c.parts
                        .filter((p) => p.bold)
                        .map((p) => p.text)
                        .join(' · ')
                    )
                  }
                >
                  {ch.label} 📄
                </button>
              ))}
              {c.not_booked ? (
                <span className="cn-st st-rec">NOT BOOKED</span>
              ) : (
                <span className={`cn-st ${BADGE[c.state].cls}`}>{BADGE[c.state].label}</span>
              )}
            </span>
          </div>
        ))}

        {notice ? (
          <div className="cn-note" style={{ padding: '0 16px' }}>
            {notice}
          </div>
        ) : null}
        {error ? <div className="cn-errline">{error}</div> : null}
        {deckChanged ? (
          <div style={{ padding: '4px 16px' }}>
            <button
              type="button"
              className="btn p"
              data-testid="deck-changed-reload"
              disabled={busy}
              onClick={() => {
                setError(null)
                setDeckChanged(false)
                onRefresh?.()
              }}
            >
              Reload card to see what changed
            </button>
            {/* Guide (2026-09-02): the refusal is a new situation for the
             * officer — the ? explains what happened and what Reload does. */}
            <HelpDot k="deck.changed" />
          </div>
        ) : null}

        {discardOpen ? (
          <div style={{ padding: '4px 16px' }}>
            <textarea
              value={discardReason}
              onChange={(e) => setDiscardReason(e.target.value)}
              placeholder="Why does this card not book? Recorded under your name."
              style={{
                width: '100%',
                minHeight: 52,
                font: 'inherit',
                fontSize: 'var(--text-12)',
                border: '1px solid var(--cn-line)',
                borderRadius: 8,
                padding: 8,
              }}
              data-testid="discard-reason"
            />
          </div>
        ) : null}

        <div className="cn-abar">
          {isMergeQuestion ? (
            <>
              {/* Bias to separate (R1, Ale 2026-09-04: "shares" and
               * "limited partnership interests" are NOT the same). The
               * safe answer is the primary; merging folds two records
               * into one and is the harder thing to undo. */}
              <button
                type="button"
                className="cn-btn p"
                disabled={busy}
                onClick={submitKeepSeparate}
                data-testid="claims-keep-separate"
              >
                Different — keep separate
              </button>
              <button
                type="button"
                className="cn-btn"
                disabled={busy}
                onClick={submitMerge}
                data-testid="claims-merge"
              >
                Same — merge
              </button>
            </>
          ) : discardOpen ? (
            <>
              <button
                type="button"
                className="cn-btn p"
                disabled={busy || !discardReason.trim()}
                onClick={submitDiscard}
              >
                Discard — reason stands
              </button>
              <button type="button" className="cn-btn" onClick={() => setDiscardOpen(false)}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="cn-btn p"
                disabled={busy || pickRequired}
                onClick={() =>
                  submitApprove(
                    bookClaims.length === 0 && tickableHolders.length ? 'continue' : 'approve'
                  )
                }
                data-testid="claims-approve"
              >
                {primaryLabel}
              </button>
              <button
                type="button"
                className="cn-btn"
                onClick={() => setEditing((e) => !e)}
                data-testid="claims-edit"
              >
                {editing ? 'Done' : 'Edit'}
              </button>
              <button
                type="button"
                className="cn-btn"
                onClick={() => setDiscardOpen(true)}
                data-testid="claims-reject"
              >
                Reject
              </button>
              {card.task_id ? (
                <span style={{ position: 'relative' }}>
                  <button type="button" className="cn-btn" onClick={() => setMenuOpen((m) => !m)}>
                    ⋯
                  </button>
                  {menuOpen ? (
                    <button
                      type="button"
                      className="cn-btn"
                      style={{ marginLeft: 6 }}
                      disabled={busy}
                      onClick={submitSnooze}
                    >
                      Snooze until the next document
                    </button>
                  ) : null}
                </span>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function ClaimPartView({
  part,
  editing,
  value,
  onEdit,
}: {
  part: ClaimPart
  editing: boolean
  value: string
  onEdit: (v: string) => void
}) {
  const cls = [part.bold ? 'b' : '', part.small ? 's' : ''].join(' ')
  if (part.small) return <small>{part.text}</small>
  if (editing) {
    return (
      <span
        className={`cn-ed ${cls}`}
        style={part.bold ? { fontWeight: 700 } : undefined}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onEdit((e.target as HTMLElement).textContent ?? '')}
      >
        {part.text}
      </span>
    )
  }
  return part.bold ? <b>{value !== part.text ? value : part.text}</b> : <span>{part.text}</span>
}

/* ── the ownership chart ─────────────────────────────────────────────── */

function ChartView({
  chart,
  ticks,
  onTick,
  hover,
  setHover,
  override,
}: {
  chart: SegmentChart
  ticks: Record<string, boolean>
  onTick: (key: string) => void
  hover: string | null
  setHover: (h: string | null) => void
  override: (key: string, kind: 'pos' | 'trd') => string | null
}) {
  const multi = chart.holders.length > 1
  const chartRef = useRef<HTMLDivElement>(null)
  const colRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [bar, setBar] = useState<{ left: number; width: number; top: number } | null>(null)
  const [shift, setShift] = useState(0)
  const colsRef = useRef<HTMLDivElement>(null)

  const tickedKeys = chart.holders.filter((h) => ticks[h.key] ?? h.ticked ?? true).map((h) => h.key)

  useLayoutEffect(() => {
    if (!multi || !chartRef.current || !colsRef.current) return
    const cr = chartRef.current.getBoundingClientRect()
    const centers = tickedKeys
      .map((k) => colRefs.current[k])
      .filter((el): el is HTMLDivElement => !!el)
      .map((el) => {
        const r = el.getBoundingClientRect()
        return r.left + r.width / 2 - cr.left
      })
    if (!centers.length) {
      setBar(null)
      setShift(0)
      return
    }
    const min = Math.min(...centers)
    const max = Math.max(...centers)
    const mid = (min + max) / 2
    const colsBottom = colsRef.current.getBoundingClientRect().bottom - cr.top
    setBar(centers.length > 1 ? { left: min, width: max - min, top: colsBottom - 1 } : null)
    setShift(mid - chartRef.current.clientWidth / 2)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multi, tickedKeys.join('|')])

  const stack = (
    <>
      <div
        className={`cn-nbox i${hover === 'instrument' ? ' cn-hl' : ''}`}
        onMouseEnter={() => setHover('instrument')}
        onMouseLeave={() => setHover(null)}
        data-testid="chart-instrument"
      >
        <div className="nm">{chart.instrument_name ?? 'instrument'}</div>
      </div>
      <div className="cn-edge">
        <div className="cn-wire ins" />
        {chart.issued_label ? (
          <div className="cn-elab">
            <div className={`ibal${hover === 'instrument' ? ' cn-hlt' : ''}`}>
              {chart.issued_label}
            </div>
          </div>
        ) : null}
      </div>
      <div
        className={`cn-nbox e${hover === 'issuer' ? ' cn-hl' : ''}`}
        onMouseEnter={() => setHover('issuer')}
        onMouseLeave={() => setHover(null)}
        data-testid="chart-issuer"
      >
        <div className="nm">{chart.issuer_name ?? 'issuer'}</div>
      </div>
    </>
  )

  if (!multi) {
    const h = chart.holders[0]
    return (
      <div className="cn-chart" data-testid="segment-chart">
        {h ? (
          <>
            <div
              className={`cn-nbox e${hover === h.key ? ' cn-hl' : ''}`}
              onMouseEnter={() => setHover(h.key)}
              onMouseLeave={() => setHover(null)}
              data-testid="chart-holder"
            >
              {h.yours ? <span className="you">YOURS</span> : null}
              <div className="nm">{h.name}</div>
            </div>
            <div className="cn-edge">
              <div className="cn-wire pos" />
              <div className="cn-elab">
                {h.position_label ? (
                  <div className={`pos${hover === `pos:${h.key}` ? ' cn-hlt' : ''}`}>
                    {override(h.key, 'pos') ?? h.position_label}
                  </div>
                ) : null}
                {h.trade_label ? (
                  <div
                    className={`trd${h.trade_moves === 'instrument' ? ' ti' : ''}${
                      hover === `trd:${h.key}` ? ' cn-hlt' : ''
                    }`}
                  >
                    <span className="a">➜</span> {override(h.key, 'trd') ?? h.trade_label}
                  </div>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
        {stack}
      </div>
    )
  }

  return (
    <div className="cn-chart" ref={chartRef} data-testid="segment-chart">
      <div className="cn-cols" ref={colsRef}>
        {chart.holders.map((h) => {
          const on = ticks[h.key] ?? h.ticked ?? true
          return (
            <div key={h.key} className={`cn-col${on ? '' : ' dim'}`}>
              <div
                ref={(el) => {
                  colRefs.current[h.key] = el
                }}
                className={`cn-nbox e${h.tickable ? ' clickable' : ''}${
                  hover === h.key ? ' cn-hl' : ''
                }`}
                style={{ minWidth: 0, alignSelf: 'stretch' }}
                onClick={h.tickable ? () => onTick(h.key) : undefined}
                onMouseEnter={() => setHover(h.key)}
                onMouseLeave={() => setHover(null)}
                data-testid="chart-holder"
                data-holder-ticked={on}
              >
                {h.tickable ? <span className={`cn-linkcb${on ? ' on' : ''}`} /> : null}
                {h.yours ? <span className="you">YOURS</span> : null}
                <div className="nm">{h.name}</div>
              </div>
              <div className="cn-edge" style={{ minHeight: 30 }}>
                {on ? <div className="cn-wire pos" style={{ minHeight: 30 }} /> : null}
                <div className="cn-elab">
                  {h.position_label ? (
                    <div className={on ? 'pos' : 'unlinked'}>
                      {override(h.key, 'pos') ?? h.position_label}
                    </div>
                  ) : null}
                  {h.trade_label ? (
                    <div className={on ? 'trd' : 'unlinked'}>
                      {on ? <span className="a">➜ </span> : null}
                      {override(h.key, 'trd') ?? h.trade_label}
                    </div>
                  ) : null}
                  {h.note && h.tickable ? (
                    <div className={on ? 'booksnote' : 'namednote'}>
                      {on ? 'books on approve' : h.note}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {bar ? (
        <div className="cn-hbar pos" style={{ left: bar.left, width: bar.width, top: bar.top }} />
      ) : null}
      <div className="cn-stack" style={{ transform: `translateX(${shift}px)` }}>
        <div className="cn-awire pos" style={{ height: 22 }} />
        {stack}
      </div>
    </div>
  )
}
